"use client";

import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader, Check } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
function IrisMark({
  className = ""
}: {
  className?: string;
}) {
  return <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className={className} aria-hidden="true">
      <circle cx="12" cy="12" r="10.5" stroke="currentColor" strokeWidth="1" />
      <circle cx="12" cy="12" r="6" stroke="currentColor" strokeWidth="1" />
      <circle cx="12" cy="12" r="2" fill="currentColor" />
    </svg>;
}
function InquiryForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="bg-card border border-border p-10 md:p-12 relative shadow-md">
        <span className="absolute top-0 left-0 w-3.5 h-px bg-primary" aria-hidden="true" />
        <span className="absolute top-0 left-0 w-px h-3.5 bg-primary" aria-hidden="true" />
        <span className="absolute bottom-0 right-0 w-3.5 h-px bg-primary" aria-hidden="true" />
        <span className="absolute bottom-0 right-0 w-px h-3.5 bg-primary" aria-hidden="true" />

        <div className="flex flex-col items-start gap-6">
          <div className="flex items-center gap-3 text-primary">
            <Check className="size-5" strokeWidth={1.25} />
            <span className="font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground">
              RSVP Confirmed
            </span>
          </div>
          <h3 className="font-default text-3xl md:text-4xl text-foreground leading-tight">
            We can&apos;t wait to <em className="text-primary font-normal">celebrate</em> with you.
          </h3>
          <p className="text-muted-foreground leading-relaxed max-w-md font-sans text-sm">
            {successMessage}
          </p>
          <div className="flex items-center gap-2 mt-2">
            <span className="h-px w-16 bg-primary" aria-hidden="true" />
            <span className="size-1 bg-primary rounded-md" aria-hidden="true" />
          </div>
          <Button type="button" onClick={resetForm} variant="outline" className="mt-2 rounded-md border-primary text-primary hover:bg-primary hover:text-primary-foreground uppercase tracking-[0.2em] text-xs px-9 py-4 h-auto font-sans font-bold">
            Update your response
          </Button>
        </div>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-10">
      <FormField control={control} name="name" render={({
      field
    }) => <FormItem>
            <FormLabel className="font-mono text-[0.75rem] uppercase tracking-[0.2em] text-muted-foreground">
              Guest Name
            </FormLabel>
            <FormControl>
              <Input placeholder="Your full name" {...field} className="bg-transparent border-0 border-b border-border rounded-md px-0 py-4 h-auto text-foreground placeholder:text-muted-foreground/60 focus-visible:border-primary focus-visible:ring-0 transition-all duration-[600ms] font-sans text-base" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="email" render={({
      field
    }) => <FormItem>
            <FormLabel className="font-mono text-[0.75rem] uppercase tracking-[0.2em] text-muted-foreground">
              Email Address
            </FormLabel>
            <FormControl>
              <Input type="email" placeholder="hello@example.com" {...field} className="bg-transparent border-0 border-b border-border rounded-md px-0 py-4 h-auto text-foreground placeholder:text-muted-foreground/60 focus-visible:border-primary focus-visible:ring-0 transition-all duration-[600ms] font-sans text-base" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="phone" render={({
      field
    }) => <FormItem>
            <FormLabel className="font-mono text-[0.75rem] uppercase tracking-[0.2em] text-muted-foreground">
              Phone Number
            </FormLabel>
            <FormControl>
              <Input type="tel" placeholder="For event updates" {...field} className="bg-transparent border-0 border-b border-border rounded-md px-0 py-4 h-auto text-foreground placeholder:text-muted-foreground/60 focus-visible:border-primary focus-visible:ring-0 transition-all duration-[600ms] font-sans text-base" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="placement" render={({
      field
    }) => <FormItem>
            <FormLabel className="font-mono text-[0.75rem] uppercase tracking-[0.2em] text-muted-foreground">
              Dietary Requirements
            </FormLabel>
            <FormControl>
              <Input placeholder="Vegetarian, allergies, etc..." {...field} className="bg-transparent border-0 border-b border-border rounded-md px-0 py-4 h-auto text-foreground placeholder:text-muted-foreground/60 focus-visible:border-primary focus-visible:ring-0 transition-all duration-[600ms] font-sans text-base" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="size" render={({
      field
    }) => <FormItem>
            <FormLabel className="font-mono text-[0.75rem] uppercase tracking-[0.2em] text-muted-foreground">
              Number of Guests
            </FormLabel>
            <FormControl>
              <Select onValueChange={field.onChange} value={field.value}>
                <SelectTrigger className="bg-transparent border-0 border-b border-border rounded-md px-0 py-4 h-auto text-foreground focus-visible:border-primary focus-visible:ring-0 transition-all duration-[600ms] font-sans text-base w-full data-[placeholder]:text-muted-foreground/60">
                  <SelectValue placeholder="Select party size" />
                </SelectTrigger>
                <SelectContent className="bg-popover border-border rounded-md">
                  <SelectItem value="small">Just me</SelectItem>
                  <SelectItem value="medium">Me + 1 guest</SelectItem>
                  <SelectItem value="large">Family (3+)</SelectItem>
                </SelectContent>
              </Select>
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="references" render={({
      field
    }) => <FormItem>
            <FormLabel className="font-mono text-[0.75rem] uppercase tracking-[0.2em] text-muted-foreground">
              Song Request
            </FormLabel>
            <FormControl>
              <Textarea rows={5} placeholder="What song will get you on the dance floor?" {...field} className="bg-transparent border-0 border-b border-border rounded-md px-0 py-4 text-foreground placeholder:text-muted-foreground/60 focus-visible:border-primary focus-visible:ring-0 transition-all duration-[600ms] font-sans text-base resize-none" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="concept" render={({
      field
    }) => <FormItem>
            <FormLabel className="font-mono text-[0.75rem] uppercase tracking-[0.2em] text-muted-foreground">
              Message for the Parents
            </FormLabel>
            <FormControl>
              <Textarea rows={8} placeholder="Share a sweet note or advice for the Ohlsons..." {...field} className="bg-transparent border-0 border-b border-border rounded-md px-0 py-4 text-foreground placeholder:text-muted-foreground/60 focus-visible:border-primary focus-visible:ring-0 transition-all duration-[600ms] font-sans text-base resize-none" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="availability" render={({
      field
    }) => <FormItem>
            <FormLabel className="font-mono text-[0.75rem] uppercase tracking-[0.2em] text-muted-foreground">
              Will you be attending?
            </FormLabel>
            <FormControl>
              <RadioGroup onValueChange={field.onChange} value={field.value} className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-2">
                <label className="flex items-center gap-3 cursor-pointer group">
                  <RadioGroupItem value="weekday" className="border-border text-primary data-[state=checked]:border-primary" />
                  <span className="font-sans text-sm text-foreground group-hover:text-primary transition-colors duration-300">
                    Joyfully Accept
                  </span>
                </label>
                <label className="flex items-center gap-3 cursor-pointer group">
                  <RadioGroupItem value="weekend" className="border-border text-primary data-[state=checked]:border-primary" />
                  <span className="font-sans text-sm text-foreground group-hover:text-primary transition-colors duration-300">
                    Regretfully Decline
                  </span>
                </label>
                <label className="flex items-center gap-3 cursor-pointer group">
                  <RadioGroupItem value="either" className="border-border text-primary data-[state=checked]:border-primary" />
                  <span className="font-sans text-sm text-foreground group-hover:text-primary transition-colors duration-300">
                    Still Deciding
                  </span>
                </label>
              </RadioGroup>
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="consent" render={({
      field
    }) => <FormItem>
            <FormControl>
              <label className="flex items-start gap-3 cursor-pointer group pt-2">
                <input type="checkbox" checked={!!field.value} onChange={e => field.onChange(e.target.checked)} className="mt-1 accent-primary size-4 cursor-pointer" />
                <span className="font-sans text-sm text-foreground leading-relaxed">
                  I confirm my attendance details.
                  <FormDescription className="text-muted-foreground font-sans text-xs mt-1">
                    We will use this information to finalize the brunch catering.
                  </FormDescription>
                </span>
              </label>
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 border border-destructive/40 bg-destructive/10 rounded-md">
          <p className="text-destructive font-sans text-sm">{submitError}</p>
        </div>}

      <div className="pt-4">
        <Button type="submit" disabled={isSubmitting} className="w-full bg-primary text-primary-foreground hover:bg-primary/90 rounded-md uppercase tracking-[0.2em] text-xs px-9 py-5 h-auto font-sans font-bold transition-all duration-300 group relative overflow-hidden">
          {isSubmitting ? <>
              <Loader className="animate-spin mr-2 size-4" strokeWidth={1.25} />
              Sending...
            </> : <>
              <span>Send Your Love</span>
              <span className="ml-3 inline-flex items-center gap-1.5 transition-all duration-300 group-hover:translate-x-1">
                <span className="h-px w-4 bg-primary-foreground group-hover:w-6 transition-all duration-300" aria-hidden="true" />
                <span className="size-1 bg-primary-foreground rounded-md" aria-hidden="true" />
              </span>
            </>}
        </Button>
        <p className="mt-6 font-mono text-[0.75rem] uppercase tracking-[0.2em] text-muted-foreground">
          — Please RSVP by October 15th. We can&apos;t wait to see you.
        </p>
      </div>
    </Form>;
}
export default function SendYourLove() {
  const reveal = useRevealOnScroll<HTMLDivElement>({
    once: true,
    threshold: 0.15
  });
  const processSteps = [{
    index: "01",
    label: "RSVP Online"
  }, {
    index: "02",
    label: "Check the Registry"
  }, {
    index: "03",
    label: "Join us for Brunch"
  }];
  return <section data-section-id="1626" id="send-your-love" className="bg-background text-foreground py-32 md:py-44 relative overflow-hidden">
      {}
      <div className="absolute top-0 left-0 right-0 flex items-center" aria-hidden="true">
        <span className="size-1 bg-primary rounded-md" />
        <span className="flex-1 h-px bg-border" />
        <span className="size-1 bg-primary rounded-md" />
      </div>

      <div ref={reveal.ref} className={`max-w-[1440px] mx-auto px-6 md:px-12 transition-all duration-[1200ms] ease-out ${reveal.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {}
          <div className="lg:col-span-5 flex flex-col">
            {}
            <div className="flex items-center gap-3 mb-12">
              <span className="font-mono text-[0.75rem] uppercase tracking-[0.2em] text-muted-foreground">
                § 04 — RSVP · Welcome Baby Ohlson
              </span>
            </div>

            {}
            <h2 className="font-default text-5xl md:text-6xl lg:text-7xl leading-[1.1] tracking-tight text-foreground mb-10">
              Send your <em className="text-primary font-normal italic">love</em>.
            </h2>

            {}
            <p className="font-serif text-xl md:text-2xl font-light leading-[1.6] text-foreground/90 mb-12 italic">
              A new chapter is beginning for our family. We would be honored to have you join us for a morning of celebration, laughter, and sweet anticipation.
            </p>

            {}
            <div className="flex flex-col items-start mb-12" aria-hidden="true">
              <span className="w-px h-[120px] bg-border" />
              <span className="size-1 bg-primary rounded-md -mt-px" />
            </div>

            {}
            <div className="space-y-5">
              {processSteps.map((step, i) => <div data-index={i} key={step.index} className="flex items-center gap-4">
                  <IrisMark className="text-primary shrink-0" />
                  <span className="font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground">
                    {step.index} ·
                  </span>
                  <span className="font-mono text-xs uppercase tracking-[0.2em] text-foreground">
                    {step.label}
                  </span>
                </div>)}
            </div>
          </div>

          {}
          <div className="lg:col-span-7">
            <div className="max-w-2xl lg:ml-auto">
              <FormProvider formKey="send_love_form_home" formId="send_love_form_home" sectionName="send-your-love" defaultValues={{
              name: "",
              email: "",
              phone: "",
              placement: "",
              size: "",
              references: "",
              concept: "",
              availability: "",
              consent: false
            }} validationRules={{
              name: {
                required: true,
                minLength: 2,
                maxLength: 80
              },
              email: {
                required: true,
                pattern: "email"
              },
              phone: {
                required: true,
                minLength: 5
              },
              placement: {
                required: true,
                minLength: 2
              },
              size: {
                required: true
              },
              references: {
                minLength: 0
              },
              concept: {
                required: true,
                minLength: 10
              },
              availability: {
                required: true
              },
              consent: {
                required: true
              }
            }} submitText="Send Your Love" successMessage="Your RSVP has been received. We are so excited to celebrate this special day with you! Look out for a confirmation email with venue details shortly." errorMessage="We couldn't process your RSVP. Please try again or contact the hosts directly at hello@ohlsonfamily.com.">
                <div className="relative bg-card border border-border p-6 sm:p-10 md:p-12 shadow-md">
                  {}
                  <span className="absolute top-0 left-0 w-3.5 h-px bg-primary" aria-hidden="true" />
                  <span className="absolute top-0 left-0 w-px h-3.5 bg-primary" aria-hidden="true" />
                  <span className="absolute top-0 right-0 w-3.5 h-px bg-primary" aria-hidden="true" />
                  <span className="absolute top-0 right-0 w-px h-3.5 bg-primary" aria-hidden="true" />
                  <span className="absolute bottom-0 left-0 w-3.5 h-px bg-primary" aria-hidden="true" />
                  <span className="absolute bottom-0 left-0 w-px h-3.5 bg-primary" aria-hidden="true" />
                  <span className="absolute bottom-0 right-0 w-3.5 h-px bg-primary" aria-hidden="true" />
                  <span className="absolute bottom-0 right-0 w-px h-3.5 bg-primary" aria-hidden="true" />

                  <InquiryForm />
                </div>
              </FormProvider>
            </div>
          </div>
        </div>
      </div>
    </section>;
}